/**
 * Where the VERKEHR.NRW proxy is reachable, or empty when there is none.
 *
 * The map images come from a service that sends no CORS header, so the browser cannot fetch them
 * directly and a proxy on our own side has to pass them through. The add-in is also published as
 * a static site, which cannot run one.
 *
 * The absence of this value *is* the switch: with no proxy there is no NRW basemap and no traffic
 * overlay anywhere in the interface. A separate on/off flag alongside a URL would allow the state
 * "switched on, pointing nowhere", which is a control that does nothing and no way to tell why.
 */
const PROXY_BASE = ((import.meta.env.VITE_TRAFFIC_PROXY_BASE as string | undefined) ?? "")
  .trim()
  .replace(/\/+$/, "");

/** Whether this deployment can show anything from VERKEHR.NRW at all. */
export const HAS_TRAFFIC_PROXY = PROXY_BASE.length > 0;

/** What the traffic overlay can show. The proxy allows exactly these two. */
export type TrafficLayerKey = "congestion" | "roadworks";

const WMS_LAYER_BY_KEY: Record<TrafficLayerKey, string> = {
  congestion: "LOS",
  roadworks: "traffic_roadworks_lines",
};

export const TRAFFIC_LAYER_KEYS: TrafficLayerKey[] = ["congestion", "roadworks"];

/** Tiles of the NRW basemap. Two of them: the roads, and the labels that go over everything. */
export function nrwBasemapTileUrl(layer: "osm_tiles" | "osm_tiles_foreground"): string {
  return `${PROXY_BASE}/map-tiles/verkehr-nrw/${layer}/{z}/{x}/{y}.png`;
}

/**
 * Tiles of the ÖPNV map, through the same proxy: memomaps.de sends no CORS header either, and
 * the map page draws with WebGL, which refuses images from another origin without one.
 */
export function publicTransportTileUrl(): string {
  return `${PROXY_BASE}/map-tiles/public-transport/{z}/{x}/{y}.png`;
}

/**
 * A traffic overlay as tiles MapLibre can request.
 *
 * The service speaks WMS, which answers for a bounding box rather than for a numbered tile, so
 * MapLibre is asked for raster tiles and fills in the box itself. 256 pixels a side keeps each
 * request well inside the size the proxy allows.
 */
function buildTrafficTileUrl(key: TrafficLayerKey): string {
  const query = new URLSearchParams({
    service: "WMS",
    version: "1.1.1",
    request: "GetMap",
    layers: WMS_LAYER_BY_KEY[key],
    styles: "",
    format: "image/png",
    transparent: "true",
    srs: "EPSG:3857",
    width: "256",
    height: "256",
  });

  // The placeholder has to survive as-is; encoding it would leave MapLibre nothing to replace.
  return `${PROXY_BASE}/traffic/wms?${query.toString()}&bbox={bbox-epsg-3857}`;
}

/**
 * The tile address of each traffic overlay, built once.
 *
 * The addresses cannot change while the page is open — the proxy's base comes from the build —
 * and the map asks for them inside its render, which on a moving fleet runs sixty times a
 * second.
 */
const TRAFFIC_TILE_URLS: Record<TrafficLayerKey, string> = {
  congestion: buildTrafficTileUrl("congestion"),
  roadworks: buildTrafficTileUrl("roadworks"),
};

/** The tile address of one traffic overlay, as MapLibre wants it. */
export function trafficTileUrl(key: TrafficLayerKey): string {
  return TRAFFIC_TILE_URLS[key];
}
